import { Select } from 'src/design/atoms/Select';
import moment from 'moment';
import React from 'react';
import {
  formatWorkWeekId,
  getCurrentWorkWeek,
  getWorkWeekRange,
  parseWorkWeekId,
} from 'src/utils/workWeek';
import { formatDate } from 'src/utils';
import { IDateOption } from 'src/routes/Jobs/Timesheets/types';

const currentWorkWeek = getCurrentWorkWeek();
const currentYear = currentWorkWeek.year;

// Current year + previous 2 years, so timesheet history stays browsable across a year boundary.
const YEARS_OF_HISTORY = 2;

const buildWeekOptions = (): IDateOption[] => {
  const options: IDateOption[] = [];
  for (let yearOffset = YEARS_OF_HISTORY; yearOffset >= 0; yearOffset--) {
    const year = currentYear - yearOffset;
    for (let weekNum = 1; weekNum <= 52; weekNum++) {
      const range = getWorkWeekRange(weekNum, year);
      const start = moment(range.start).hour(12).toDate();
      const end = moment(range.end).hour(12).toDate();
      options.push({
        id: formatWorkWeekId(year, weekNum),
        startDate: start,
        endDate: end,
        prefix:
          year === currentWorkWeek.year &&
          weekNum === currentWorkWeek.weekNumber
            ? 'Current Week - '
            : undefined,
        label: `${formatDate(start, 'ddd (M/D/YY)')} - ${formatDate(
          end,
          'ddd (M/D/YY)',
        )}`,
      });
    }
  }
  return options;
};

export const tempOptions: IDateOption[] = buildWeekOptions();

export const currentWeekFilter: IDateOption =
  tempOptions.find(
    (o) =>
      o.id ===
      formatWorkWeekId(currentWorkWeek.year, currentWorkWeek.weekNumber),
  ) ?? tempOptions[0];

const WeekOptions = [...tempOptions];

interface IWeekSelectorProps {
  value: IDateOption;
  onChange: (value: IDateOption | null) => void;
  hideLabel?: boolean;
}

export const WeekSelector: React.FC<IWeekSelectorProps> = ({
  onChange,
  value,
  hideLabel = false,
}) => {
  const weekData = React.useMemo(() => {
    return WeekOptions.map((week) => {
      const { weekNumber } = parseWorkWeekId(week.id);
      return {
        value: week.id,
        label: `${week.prefix ? week.prefix : ''} Week ${weekNumber}:  ${
          week.label
        }`,
      };
    });
  }, []);

  const handleChange = (selectedValue: string | null) => {
    if (selectedValue) {
      const selectedWeek =
        WeekOptions.find((w) => w.id === selectedValue) || null;
      if (selectedWeek) {
        onChange(selectedWeek);
      }
    } else {
      onChange(null);
    }
  };

  return (
    <Select
      placeholder="Select weeks..."
      data={weekData}
      value={value?.id || null}
      onChange={handleChange}
      searchable
      clearable
      label={!hideLabel ? 'Work Week' : undefined}
    />
  );
};
